import React from "react";

import { env } from "~/env";
import { Step } from "~/screens/get-started/qualifier-questions/-components/step";
import { Skeleton } from "~/screens/Scheduling/InitialVisit/components/SkeletonElements";
import { RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS } from "~/screens/Scheduling/PersonalInformation/components/VisitForm/constants";
import { FILL_OPTION, FILL_SWITCH_OPTIONS, SwitchVariant } from "~/services";
import { useReferralSourcesListQuery } from "~/services/referral-sources";
import { useStatesListQuery } from "~/services/states/actions";
import { OTHER_STATES_OPTIONS } from "~/shared.types";
import { useUserStore } from "~/stores";
import { usePatientStore } from "~/stores/use-patient-store";
import { ErrorText } from "~/ui";
import {
  relationshipToPolicyHolderOptions,
  sexAtBirthOptions,
} from "../../data/constants";
import { ConsentCheckbox } from "../consent-checkbox";
import { FileUpload } from "../file-upload";
import { FormInput } from "../form-input";
import { FormSection } from "../form-section";
import { SubmissionCardSwitch } from "../insurance-submission-switch";
import { useVisitFormContext } from "./visit-form";

const getErrorMessage = (error: any): string | undefined => {
  return typeof error === "string" ? error : error?.message;
};

const useFieldProps = () => {
  const { form, fieldErrorVisibility } = useVisitFormContext();
  const {
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = form;
  const watchedValues = watch();
  const { isErrorVisible, hideError, showError } = fieldErrorVisibility;

  return (field: string, transformValue?: (value: string) => string) => ({
    value: watchedValues[field] ?? "",
    onChange: (value: string) => {
      hideError(field);
      setValue(field as any, transformValue ? transformValue(value) : value, {
        shouldValidate: true,
      });
    },
    onBlur: () => {
      showError(field);
      void trigger(field as any);
    },
    error: isErrorVisible(field) ? getErrorMessage(errors[field]) : undefined,
  });
};

export function GeneralInfoSection() {
  const fieldProps = useFieldProps();

  return (
    <FormSection title="GENERAL INFORMATION">
      <div className="grid grid-cols-2 gap-2">
        <FormInput
          label="First name"
          name="firstName"
          type="text"
          placeholder="E.g. Jane"
          required
          {...fieldProps("firstName")}
        />

        <FormInput
          label="Last name"
          name="lastName"
          type="text"
          placeholder="E.g. Smith"
          required
          {...fieldProps("lastName")}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <FormInput
          label="Date of birth"
          name="dateOfBirth"
          type="dob"
          required
          {...fieldProps("dateOfBirth")}
        />

        <FormInput
          label="Sex at birth"
          name="sexAtBirth"
          type="select"
          options={sexAtBirthOptions}
          required
          {...fieldProps("sexAtBirth")}
        />
      </div>
    </FormSection>
  );
}

export function ContactInfoSection() {
  const fieldProps = useFieldProps();

  return (
    <FormSection title="CONTACT INFORMATION">
      <FormInput
        label="Mobile phone number"
        name="mobilePhone"
        type="tel"
        required
        {...fieldProps("mobilePhone")}
      />

      <p className="rounded-lg bg-gray-50 font-sans text-xs leading-4 text-gray-600">
        By entering your phone number, you consent to receive messages and phone
        calls from Elektra Health. Message and data rates may apply. Reply STOP
        to opt out.
      </p>
    </FormSection>
  );
}

export function AddressSection({
  isRequired = true,
  hideSectionTitle = false,
  isZipCodeRequired = isRequired,
}: {
  isRequired?: boolean;
  hideSectionTitle?: boolean;
  isZipCodeRequired?: boolean;
}) {
  const { data: statesList, isLoading: isStatesListLoading } =
    useStatesListQuery();
  const fieldProps = useFieldProps();

  const content = (
    <>
      <FormInput
        label="Home address 1"
        name="homeAddress1"
        type="text"
        required={isRequired}
        {...fieldProps("homeAddress1")}
      />

      <FormInput
        label="Home address 2"
        name="homeAddress2"
        type="text"
        {...fieldProps("homeAddress2")}
      />

      <div className="grid grid-cols-1 gap-2 space-y-2">
        <FormInput
          label="City"
          name="city"
          type="text"
          required={isRequired}
          {...fieldProps("city")}
        />

        <div className="grid grid-cols-2 gap-2">
          {isStatesListLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <FormInput
              label="State"
              name="state"
              type="select"
              options={statesList?.map((state) => ({
                value: state.id.toString(),
                label: state.name,
              }))}
              required={isRequired}
              {...fieldProps("state")}
            />
          )}

          <FormInput
            label="Zip code"
            name="zipCode"
            type="text"
            required={isZipCodeRequired}
            {...fieldProps("zipCode")}
          />
        </div>
      </div>
    </>
  );

  if (hideSectionTitle) return content;

  return <FormSection title="HOME ADDRESS">{content}</FormSection>;
}

export function ReferralSourceSection() {
  const { data: referralSources } = useReferralSourcesListQuery();
  const fieldProps = useFieldProps();

  return (
    <FormSection title="HOW DID YOU HEAR ABOUT ELEKTRA?">
      <FormInput
        label="How did you hear about Elektra?"
        hideLabel
        name="referralSource"
        type="select"
        options={referralSources?.map((source) => ({
          value: source.id.toString(),
          label: source.name,
        }))}
        placeholder="Please select"
        {...fieldProps("referralSource")}
      />
    </FormSection>
  );
}

export type InsuranceSectionOptions = {
  hasPlan?: boolean;
  hasFileSizeError?: boolean;
  cardsRequired?: boolean;
  fieldsRequired?: boolean;
  showGroupNumber?: boolean;
};

export function InsuranceSection({
  hideSectionTitle = false,
  hasPlan,
  hasFileSizeError: hasFileSizeErrorProp,
  cardsRequired = true,
  fieldsRequired = true,
  showGroupNumber = false,
}: {
  hideSectionTitle?: boolean;
} & InsuranceSectionOptions) {
  const {
    form,
    insuranceCardFront,
    insuranceCardBack,
    setInsuranceCardFront,
    setInsuranceCardBack,
  } = useVisitFormContext();
  const {
    watch,
    formState: { errors },
  } = form;
  const fieldProps = useFieldProps();
  const insuranceData = usePatientStore((state) => state.insuranceData);
  const storeHasFileSizeError = usePatientStore(
    (state) => state.hasFileSizeError,
  );
  const hasFileSizeError = hasFileSizeErrorProp ?? storeHasFileSizeError;
  const hasProvider = hasPlan ?? !!insuranceData;
  const relationshipToPolicyHolder = watch("relationshipToPolicyHolder");

  const showPolicyHolderInfo =
    relationshipToPolicyHolder &&
    relationshipToPolicyHolder !== RELATIONSHIP_TO_POLICY_HOLDER_OPTIONS.SELF;

  const content = (
    <>
      {!hasProvider && (
        <FormInput
          label="Insurance company name"
          name="insuranceCompanyName"
          type="text"
          placeholder="Enter your insurance company name"
          required={fieldsRequired}
          {...fieldProps("insuranceCompanyName")}
        />
      )}

      <FormInput
        label="What is your relationship to the policy holder?"
        name="relationshipToPolicyHolder"
        type="select"
        options={relationshipToPolicyHolderOptions}
        placeholder="Please select"
        required={fieldsRequired}
        {...fieldProps("relationshipToPolicyHolder")}
      />

      {showPolicyHolderInfo && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              label="Policy holder first name"
              name="policyHolderFirstName"
              type="text"
              required={fieldsRequired}
              {...fieldProps("policyHolderFirstName")}
            />

            <FormInput
              label="Policy holder last name"
              name="policyHolderLastName"
              type="text"
              required={fieldsRequired}
              {...fieldProps("policyHolderLastName")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              label="Policy holder date of birth"
              name="policyHolderDateOfBirth"
              type="dob"
              required={fieldsRequired}
              {...fieldProps("policyHolderDateOfBirth")}
            />
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <p className="font-sans text-sm text-gray-900">
            Please upload photos of your insurance card.
          </p>
        </div>

        <FileUpload
          label="Front of card"
          name="insuranceCardFront"
          file={insuranceCardFront}
          onFileChange={setInsuranceCardFront}
          required={cardsRequired}
          error={getErrorMessage(errors.insuranceCardFront)}
        />

        <FileUpload
          label="Back of card"
          name="insuranceCardBack"
          file={insuranceCardBack}
          onFileChange={setInsuranceCardBack}
          required={cardsRequired}
          error={getErrorMessage(errors.insuranceCardBack)}
        />

        {hasFileSizeError && (
          <ErrorText className="col-span-2">
            The image file is too large. Please upload a smaller one.
          </ErrorText>
        )}
      </div>

      <div className="grid grid-cols-1">
        <FormInput
          label="Member ID / Policy Number"
          name="memberId"
          type="text"
          required={fieldsRequired}
          {...fieldProps("memberId", (value) => value.trim())}
        />
      </div>

      {showGroupNumber && (
        <FormInput
          label="Group number"
          name="groupNumber"
          type="text"
          {...fieldProps("groupNumber", (value) => value.trim())}
        />
      )}
    </>
  );

  if (hideSectionTitle) return content;

  return <FormSection title="INSURANCE">{content}</FormSection>;
}

export function OptionalInsuranceSection({
  switchVariant,
  label,
  showLaterWarning = true,
  insuranceOptions,
}: {
  switchVariant?: SwitchVariant;
  label?: string;
  showLaterWarning?: boolean;
  insuranceOptions?: InsuranceSectionOptions;
}) {
  const { form, forceInsuranceNow } = useVisitFormContext();
  const {
    setValue,
    watch,
    formState: { errors },
  } = form;
  const fillInsuranceInfo = watch("fillInsuranceInfo");
  const insuranceData = usePatientStore((state) => state.insuranceData);

  if (forceInsuranceNow) {
    return (
      <FormSection title="INSURANCE">
        <InsuranceSection hideSectionTitle {...insuranceOptions} />
      </FormSection>
    );
  }

  const insuranceSwitchLabel =
    label ??
    (switchVariant === "secondary"
      ? "Has your insurance changed since your last visit?*"
      : insuranceData?.name
        ? `Would you like to enter your information for ${insuranceData.name} now or later?*`
        : undefined);

  return (
    <FormSection title="INSURANCE">
      <SubmissionCardSwitch
        options={Object.values(FILL_SWITCH_OPTIONS)}
        label={insuranceSwitchLabel}
        switchVariant={switchVariant}
        selectedOption={fillInsuranceInfo}
        onChange={(selected) => {
          setValue("fillInsuranceInfo", selected, { shouldValidate: true });
        }}
        error={getErrorMessage(errors.fillInsuranceInfo)}
      />
      {fillInsuranceInfo === FILL_OPTION.NOW && (
        <InsuranceSection hideSectionTitle {...insuranceOptions} />
      )}
      {fillInsuranceInfo === FILL_OPTION.LATER &&
        showLaterWarning &&
        switchVariant !== "secondary" && (
          <Step.Warning>
            Heads up, we’ll reach out if we need more insurance information for
            verification.
          </Step.Warning>
        )}
    </FormSection>
  );
}

export function ConsentsSection() {
  const { form } = useVisitFormContext();
  const {
    setValue,
    watch,
    formState: { errors },
  } = form;
  const watchedValues = watch();

  const handleConsentChange = (checked: boolean) => {
    setValue("privacyPolicyConsent" as any, checked, { shouldValidate: true });
  };

  return (
    <FormSection title="CONSENTS">
      <ConsentCheckbox
        checked={watchedValues.privacyPolicyConsent || false}
        onChange={handleConsentChange}
        error={getErrorMessage(errors.privacyPolicyConsent)}
      />
    </FormSection>
  );
}

export function BookingNotesSection() {
  const fieldProps = useFieldProps();

  return (
    <FormSection title="REASON FOR VISIT">
      <FormInput
        label="Booking notes"
        name="bookingNotes"
        type="textarea"
        {...fieldProps("bookingNotes")}
      />
    </FormSection>
  );
}

export function OptionalAddressSection({
  switchVariant,
}: {
  switchVariant?: SwitchVariant;
} = {}) {
  const { form } = useVisitFormContext();
  const {
    setValue,
    watch,
    formState: { errors },
  } = form;
  const fillAddressInfo = watch("fillAddressInfo");

  return (
    <FormSection title="HOME ADDRESS">
      <SubmissionCardSwitch
        options={Object.values(FILL_SWITCH_OPTIONS)}
        label="Has your home address changed since your last visit?*"
        switchVariant={switchVariant}
        selectedOption={fillAddressInfo}
        onChange={(selected) => {
          setValue("fillAddressInfo" as any, selected, {
            shouldValidate: true,
          });
        }}
        error={getErrorMessage(errors.fillAddressInfo as any)}
      />
      {fillAddressInfo === FILL_OPTION.NOW && (
        <AddressSection isRequired={true} hideSectionTitle />
      )}
    </FormSection>
  );
}

export const FORM_AUDIENCE = {
  PATIENT: "patient",
  STAFF: "staff",
} as const;

export type FormAudience = (typeof FORM_AUDIENCE)[keyof typeof FORM_AUDIENCE];

export function StateDisclaimerSection({
  stateId: stateIdProp,
  audience = FORM_AUDIENCE.PATIENT,
}: {
  stateId?: number | null;
  audience?: FormAudience;
} = {}) {
  const userStoreStateId = useUserStore((state) => state.state);
  const stateId = stateIdProp !== undefined ? stateIdProp : userStoreStateId;
  const { data: states } = useStatesListQuery();
  const stateName =
    states?.find((s) => s.id === stateId)?.name ||
    Object.values(OTHER_STATES_OPTIONS).find((o) => o.value === stateId)?.label;

  if (!stateName) return null;

  if (audience === FORM_AUDIENCE.STAFF) {
    return (
      <Step.Warning className="p-3">
        Heads up! To receive care, the patient must be physically present in{" "}
        <span className="font-semibold">{stateName}</span> at the time of the
        visit.
      </Step.Warning>
    );
  }

  return (
    <Step.Warning className="p-3">
      Important! You must be physically present in{" "}
      <span className="font-semibold">{stateName}</span> at the time of your
      visit.
    </Step.Warning>
  );
}

export function CancellationPolicySection({
  audience = FORM_AUDIENCE.PATIENT,
}: {
  audience?: FormAudience;
} = {}) {
  return (
    <FormSection title="CANCELLATION POLICY">
      <p className="text-dark-blue-gray text-sm">
        {audience === FORM_AUDIENCE.STAFF
          ? "We ask that the patient cancels at least 24 hours before the visit to avoid a "
          : "We ask that you cancel at least 24 hours before your visit to avoid a "}
        <span className="text-violet font-semibold">
          ${env.VITE_LATE_CANCELLATION_FEE} cancellation/no-show fee
        </span>
        .
      </p>
    </FormSection>
  );
}
